/**
 * Layer orchestrator — builds the active deck.gl layer array from state.
 * Watches activeLayers, events, and viewport.zoom via store subscription.
 * Debounces rebuilds at 100ms and hands off to setDeckLayers().
 */

import type { Layer } from '@deck.gl/core';
import type { NormalizedEvent } from '@/sources/base-source';
import { LAYER_REGISTRY, getLayerById } from '@/config/layers';
import { appStore } from '@/core/store';
import { filterEventsByTimeRange } from '@/utils/time-filter';
import { setDeckLayers } from '@/map/globe';
import { POINT_LAYER_IDS, createPointLayer, createPulseLayer } from './point-layer';
import { createPulseDriver } from './pulse-driver';
import { createFireLayers, createEarthquakeLayers } from './heatmap-layer';
import { ARC_LAYER_IDS, createArcLayer } from './arc-layer';
import { GEOJSON_LAYER_IDS, createGeoJsonLayer, preloadGeoJsonLayers } from './geojson-layer';
import { STATIC_POINT_LAYER_IDS, createStaticPointLayers } from './static-point-layer';
import { TRADE_ROUTE_LAYER_IDS, createTradeRouteLayers } from './trade-route-layer';
import { ICON_LAYER_IDS, createIconLayer } from './icon-layer';
import { createVesselLayers, createFlightLayers, createMilitaryFlightLayers, createMilitaryFlightClusterLayers, recordPositions, clearPositionHistory } from './track-layer';
import { createGdacsLayers, createVolcanoLayers, createTropicalCycloneLayers } from './natural-hazard-layer';
import { NOTAM_LAYER_IDS, createNotamLayers } from './notam-layer';
import { UCDP_LAYER_IDS, createUcdpLayers } from './ucdp-layer';
import { MILITARY_BASE_LAYER_IDS, createMilitaryBaseLayers } from './military-base-layer';
import { NUCLEAR_LAYER_IDS, createNuclearLayers } from './nuclear-layer';
import { CYBER_LAYER_IDS, createCyberLayers } from './cyber-layer';
import { H3_JAMMING_LAYER_IDS, createH3JammingLayer, eventsToJamHexes } from './h3-jamming-layer';
import { CONFLICT_ZONE_LAYER_IDS, createConflictZoneLayers } from './conflict-zone-layer';
import { CII_CHOROPLETH_LAYER_IDS, createCIIChoroplethLayer } from './cii-choropleth-layer';
import { SANCTIONS_CHOROPLETH_LAYER_IDS, createSanctionsChoroplethLayer } from './sanctions-choropleth-layer';
import { RADIATION_LAYER_IDS, createRadiationLayers, eventsToRadiationReadings } from './radiation-layer';
import { addWeatherRadarLayer, removeWeatherRadarLayer, updateWeatherRadar } from './weather-radar-layer';
import { getAnalysisEngine } from '@/intelligence/analysis/engine';
import { initCountryDetection } from '@/map/country-detect';
import { eventBus } from '@/core/events';
import { INFRA_STATUS_LAYER_IDS, createInfraStatusLayers } from './infrastructure-status-layer';
import { DISPLACEMENT_LAYER_IDS, createDisplacementLayers } from './displacement-layer';
import { WEATHER_LAYER_IDS, createWeatherLayers } from './weather-layer';
import { PORT_LAYER_IDS, createPortLayers } from './port-layer';
import { NEWS_MARKER_LAYER_IDS, createNewsMarkerLayers, createNewsMarkerPulseRing } from './news-marker-layer';
import { HOTSPOT_LAYER_IDS, createHotspotLayers, initHotspotLayer, destroyHotspotLayer } from './hotspot-layer';
import { MILITARY_ACTIVITY_LAYER_IDS, createMilitaryActivityLayers, isMilitaryEvent } from './military-activity-layer';
import { MILITARY_VESSEL_LAYER_IDS, createMilitaryVesselLayers, createMilitaryVesselClusterLayers } from './military-vessel-layer';
import { AIS_DENSITY_LAYER_IDS, createAisDensityLayers } from './ais-density-layer';
import { AIS_DISRUPTION_LAYER_IDS, createAisDisruptionLayers, initAisDisruptionLayer, destroyAisDisruptionLayer } from './ais-disruption-layer';
import { DARK_SHIP_LAYER_IDS, createDarkShipLayers, initDarkShipLayer, destroyDarkShipLayer } from './dark-ship-layer';
import { APT_LAYER_IDS, createAptLayers } from './apt-layer';
import { IRAN_ATTACK_LAYER_IDS, createIranAttackLayers } from './iran-attack-layer';
import { MINERALS_LAYER_IDS, createMineralsLayers } from './minerals-layer';
import { IRRADIATOR_LAYER_IDS, createIrradiatorLayers } from './irradiator-layer';
import { PREDICTION_MARKET_LAYER_IDS, createPredictionMarketLayers } from './prediction-market-layer';
import { SATELLITE_LAYER_IDS, createSatelliteLayers, createImageryFootprintLayers } from './satellite-layer';
import { fetchSatellites, type SatellitePosition } from '@/sources/built-in/satellites';
import {
  createClusterIndex,
  createClusterLayers,
  createClusterPulseLayer,
  shouldCluster,
  getClusterGroup,
  GROUP_CLUSTER_RADIUS,
  type ClusterIndex,
  type ClusterGroup,
  type ClusterResult,
} from '@/map/clustering';
import { TextLayer } from '@deck.gl/layers';
import { scheduleDeconfliction, destroyLabelDeconfliction, type LabelCandidate } from '@/map/labels';
import { rafSchedule } from '@/utils/raf-schedule';
import { getMap } from '@/map/engine';
import { runContentClustering, getContentClusters, getContentClusterVersion, destroyContentClustering } from '@/map/content-clustering';
import {
  initCountryHighlight,
  buildCountryHighlightLayer,
  destroyCountryHighlight,
} from '@/map/country-highlight';
import {
  createTerminatorLayer,
  startTerminatorUpdates,
  stopTerminatorUpdates,
} from './terminator-layer';

/** Map from source/type to matching layer IDs for event filtering. */
const SOURCE_TO_LAYER: Record<string, string[]> = {};
export const TYPE_TO_LAYER: Record<string, string[]> = {};

// Build reverse lookup from LAYER_REGISTRY
for (const def of LAYER_REGISTRY) {
  if (def.sourceId) {
    const list = SOURCE_TO_LAYER[def.sourceId] ?? [];
    list.push(def.id);
    SOURCE_TO_LAYER[def.sourceId] = list;
  }
}

// Map event types to layer IDs
TYPE_TO_LAYER['conflict'] = ['conflicts'];
TYPE_TO_LAYER['protest'] = ['protests', 'protests-cluster'];
TYPE_TO_LAYER['military'] = ['military-bases'];
TYPE_TO_LAYER['fire'] = ['fires'];
TYPE_TO_LAYER['earthquake'] = ['earthquakes'];
TYPE_TO_LAYER['disaster'] = ['gdacs'];
TYPE_TO_LAYER['weather'] = ['cyclones', 'weather-alerts', 'tropical-cyclones'];
// ais-disruption is anomaly-driven (analysis:ais-anomaly), not position-driven
TYPE_TO_LAYER['vessel_position'] = ['vessels', 'naval', 'ais-density'];
TYPE_TO_LAYER['flight_position'] = ['flights', 'military-aircraft'];
TYPE_TO_LAYER['cyber_incident'] = ['cyber-incidents', 'cyber-threats'];
TYPE_TO_LAYER['infrastructure'] = ['datacenters', 'internet-exchanges', 'power-plants', 'telecom-towers'];
TYPE_TO_LAYER['economic'] = ['stock-exchanges', 'financial-centers', 'central-banks'];
TYPE_TO_LAYER['sanction'] = ['sanctions', 'sanctions-markers'];
TYPE_TO_LAYER['diplomatic'] = ['embassies'];
TYPE_TO_LAYER['political'] = ['conflicts', 'embassies'];
TYPE_TO_LAYER['social'] = ['protests', 'protests-cluster'];
TYPE_TO_LAYER['displacement'] = ['displacement'];
TYPE_TO_LAYER['news'] = ['news-markers'];
TYPE_TO_LAYER['gps_jamming'] = ['gps-jamming'];
TYPE_TO_LAYER['internet_outage'] = ['internet-outages'];
TYPE_TO_LAYER['nasa_event'] = ['nasa-events'];
TYPE_TO_LAYER['space'] = ['spaceports', 'satellites', 'satellite-tracks'];
TYPE_TO_LAYER['ai_infrastructure'] = ['ai-datacenters'];

/**
 * Build a pre-indexed Map<layerId, events[]> for O(1) lookups per layer.
 * Replaces the old O(n*m) filterEventsForLayer approach.
 * Each event is assigned to all matching layer buckets in a single pass.
 * Exported for the globe data bridge, which needs the same event->layer
 * mapping while the flat layer system is torn down.
 */
export function buildEventIndex(events: NormalizedEvent[]): Map<string, NormalizedEvent[]> {
  const index = new Map<string, NormalizedEvent[]>();

  for (const e of events) {
    // Collect all layer IDs this event maps to
    const targetLayers = new Set<string>();

    // Match by source → layer mapping
    const sourceLayers = SOURCE_TO_LAYER[e.source];
    if (sourceLayers) {
      for (const lid of sourceLayers) targetLayers.add(lid);
    }

    // Match by type → layer mapping
    const typeLayers = TYPE_TO_LAYER[e.type];
    if (typeLayers) {
      for (const lid of typeLayers) targetLayers.add(lid);
    }

    // Tags are metadata — do not treat them as layer IDs.
    // Events map to layers only via source and type mappings above.

    // Push event into each matching layer bucket
    for (const lid of targetLayers) {
      let bucket = index.get(lid);
      if (!bucket) {
        bucket = [];
        index.set(lid, bucket);
      }
      bucket.push(e);
    }
  }

  return index;
}

// ---------------------------------------------------------------------------
// Satellite data cache — fetched asynchronously, refreshed every 60 seconds
// ---------------------------------------------------------------------------

let satellitePositions: SatellitePosition[] = [];
let satelliteFetchTimer: ReturnType<typeof setInterval> | null = null;
let satelliteFetching = false;

/** Fetch and cache satellite positions. Non-blocking — updates on next render cycle. */
function refreshSatelliteData(): void {
  if (satelliteFetching) return;
  satelliteFetching = true;
  fetchSatellites()
    .then((positions) => {
      satellitePositions = positions;
      satelliteGeneration++;
      scheduleRebuild();
    })
    .catch(() => {
      // Silently fail — keep stale data if available
    })
    .finally(() => {
      satelliteFetching = false;
    });
}

/** Current event index, rebuilt only when events change. */
let currentEventIndex = new Map<string, NormalizedEvent[]>();
/** Event generation at which the event index was last built. */
let lastEventIndexGeneration = -1;
let lastEventFilterSignature = '';

/**
 * Get pre-indexed events for a layer. Falls back to empty array.
 */
function getEventsForLayer(layerId: string): NormalizedEvent[] {
  return currentEventIndex.get(layerId) ?? [];
}

/** Cluster indices cache — rebuilt when events change. */
let clusterIndices = new Map<string, ClusterIndex>();
let lastClusterEventCount = -1;
let lastClusterGeneration = -1;
let lastClusterFilterSignature = '';
let lastClusterContentGeneration = -1;

/**
 * Event generation counter — incremented when the store's events array changes.
 * Used instead of reference equality since filterEventsByTimeRange() always returns a new array.
 */
let eventGeneration = 0;

/** Bump the event generation counter. Called from the store events subscription. */
export function bumpEventGeneration(): void {
  eventGeneration++;
}

// --- Layer-instance cache (WM catalog section 17: reuse unchanged layers) ---
//
// deck.gl matches layers by id, and an IDENTICAL instance skips prop diffing
// entirely, so returning cached instances puts unchanged layers on deck's
// fast path. Cache entries invalidate by signature; families whose data can
// change without a tracked generation (hotspots, dark ships, AIS disruptions,
// pulse, terminator, labels, country highlight) stay deliberately uncached.

const layerInstanceCache = new Map<string, { signature: string; layers: Layer[] }>();

/** Static dataset generation — bumped when async static data lands
 *  (GeoJSON preload, country geometry). */
let staticDataGeneration = 0;

/** Bump the static data generation. Exported for the async data loaders. */
export function bumpStaticDataGeneration(): void {
  staticDataGeneration++;
}

/** Satellite position generation — bumped on each successful satellite fetch. */
let satelliteGeneration = 0;

/** Analysis generation — bumped on analysis:complete (CII choropleth repaint). */
let analysisGeneration = 0;

/** Cluster query results from the last cache MISS per group — deterministic
 *  for a given cluster signature, so a cache HIT can still hand the results
 *  to the per-rebuild (uncached) protest pulse ring. */
const clusterResultsCache = new Map<string, ClusterResult[]>();

/** Return cached layer instances when the signature matches, else rebuild. */
function cachedFamily(key: string, signature: string, build: () => Layer[]): Layer[] {
  const hit = layerInstanceCache.get(key);
  if (hit && hit.signature === signature) return hit.layers;
  const built = build();
  layerInstanceCache.set(key, { signature, layers: built });
  return built;
}

/** Rebuild cluster indices when events have changed. */
function rebuildClusterIndices(
  events: NormalizedEvent[],
  activeLayerIds: string[],
  filterSignature: string,
): void {
  // Skip rebuild if the underlying events haven't changed and count matches.
  // The filter signature covers time-range/cursor changes, which hand this
  // function a differently filtered set at the same event generation.
  if (
    eventGeneration === lastClusterGeneration &&
    events.length === lastClusterEventCount &&
    filterSignature === lastClusterFilterSignature &&
    getContentClusterVersion() === lastClusterContentGeneration
  ) return;
  lastClusterGeneration = eventGeneration;
  lastClusterEventCount = events.length;
  lastClusterFilterSignature = filterSignature;
  lastClusterContentGeneration = getContentClusterVersion();

  clusterIndices = new Map();

  // Group events by cluster group
  const grouped = new Map<string, NormalizedEvent[]>();
  for (const layerId of activeLayerIds) {
    const group = getClusterGroup(layerId);
    if (!group) continue;

    const layerEvents = getEventsForLayer(layerId).filter((e) => e.location);
    const existing = grouped.get(group) ?? [];
    // Deduplicate by event ID
    const seen = new Set(existing.map((e) => e.id));
    for (const e of layerEvents) {
      if (!seen.has(e.id)) {
        existing.push(e);
        seen.add(e.id);
      }
    }
    grouped.set(group, existing);
  }

  // Pass content clusters to enhance spatial grouping with semantic awareness
  const contentClusters = getContentClusters();
  for (const [group, groupEvents] of grouped) {
    if (shouldCluster(groupEvents)) {
      clusterIndices.set(
        group,
        createClusterIndex(groupEvents, contentClusters, GROUP_CLUSTER_RADIUS[group as ClusterGroup]),
      );
    }
  }
}

/**
 * Build the active deck.gl layer array from current state.
 * Called on every debounced state change.
 */
export function buildActiveLayers(
  events: NormalizedEvent[],
  activeLayers: string[],
  zoom: number,
  eventFilterSignature = '',
): Layer[] {
  const buildStart = performance.now();
  const layers: Layer[] = [];
  const activeSet = new Set(activeLayers);

  // Cache signatures. The zoom bucket matches the rebuild trigger's
  // granularity (the store subscription fires on Math.round(zoom) change),
  // so caching per bucket does not change what a rebuild can observe.
  const zoomBucket = Math.round(zoom);
  const staticSig = `${zoomBucket}|${staticDataGeneration}`;
  const eventSig = `${eventGeneration}|${eventFilterSignature}|${zoomBucket}`;

  // Pre-index events by layer ID for O(1) lookups. The generation covers
  // store event changes; the filter signature covers time-range/cursor
  // changes, which hand us a differently filtered array at the SAME
  // generation (without it, time-slider scrubbing was a visual no-op).
  if (
    eventGeneration !== lastEventIndexGeneration ||
    eventFilterSignature !== lastEventFilterSignature
  ) {
    currentEventIndex = buildEventIndex(events);
    lastEventIndexGeneration = eventGeneration;
    lastEventFilterSignature = eventFilterSignature;
  }

  // Country hover highlight layer (rendered below data layers)
  layers.push(...buildCountryHighlightLayer());

  // Rebuild cluster indices if events changed
  rebuildClusterIndices(events, activeLayers, eventFilterSignature);

  // Record positions for track layers — only when at least one track layer is active
  const hasTrackLayer = activeSet.has('vessels') || activeSet.has('flights') ||
    activeSet.has('naval') || activeSet.has('military-aircraft');
  if (hasTrackLayer) {
    const trackEvents = events.filter(
      (e) => e.location && (e.type === 'vessel_position' || e.type === 'flight_position'),
    );
    if (trackEvents.length > 0) {
      recordPositions(trackEvents);
    }
  }

  // Track which cluster groups have been rendered to avoid duplicates
  const renderedClusterGroups = new Set<string>();

  // Polygon washes must build first so they draw underneath the markers
  // regardless of the order the user toggled layers in
  const UNDERLAY_IDS = new Set(['sanctions', 'cii-choropleth', 'conflict-zones']);
  const orderedLayerIds = [...activeLayers].sort(
    (a, b) => (UNDERLAY_IDS.has(a) ? 0 : 1) - (UNDERLAY_IDS.has(b) ? 0 : 1),
  );

  for (const layerId of orderedLayerIds) {
    const def = getLayerById(layerId);
    if (!def) continue;

    // Skip layers outside their zoom range
    if (zoom < def.minZoom || zoom > def.maxZoom) continue;

    // --- Static point layers (ScatterplotLayer from GeoJSON cache) ---
    if (STATIC_POINT_LAYER_IDS.has(layerId)) {
      layers.push(...cachedFamily(layerId, staticSig, () => createStaticPointLayers([layerId], zoom)));
      continue;
    }

    // --- Trade route layers (ArcLayer with great-circle arcs) ---
    if (TRADE_ROUTE_LAYER_IDS.has(layerId)) {
      layers.push(...cachedFamily(layerId, staticSig, () => createTradeRouteLayers([layerId], zoom)));
      continue;
    }

    // --- GeoJSON static layers (polygons, lines, polyline tracks) ---
    if (GEOJSON_LAYER_IDS.has(layerId)) {
      layers.push(...cachedFamily(layerId, staticSig, () => [createGeoJsonLayer(layerId, zoom)]));
      continue;
    }

    // --- Static-data layers (import their own data, no events needed) ---

    // Military bases — branch-specific icons (static from @/config/data)
    if (MILITARY_BASE_LAYER_IDS.has(layerId)) {
      layers.push(...cachedFamily(layerId, staticSig, () => createMilitaryBaseLayers([], zoom)));
      continue;
    }

    // Conflict zones — red polygon washes (static data + country geometry)
    if (CONFLICT_ZONE_LAYER_IDS.has(layerId)) {
      layers.push(...cachedFamily(layerId, staticSig, () => createConflictZoneLayers()));
      continue;
    }

    // Sanctions — low-alpha country wash (entity markers live on
    // 'sanctions-markers'); UNDERLAY_IDS keeps it beneath the markers
    if (SANCTIONS_CHOROPLETH_LAYER_IDS.has(layerId)) {
      layers.push(...cachedFamily(layerId, staticSig, () => createSanctionsChoroplethLayer()));
      continue;
    }

    // CII choropleth — country fills from the analysis engine's scores
    if (CII_CHOROPLETH_LAYER_IDS.has(layerId)) {
      const ciiScores = getAnalysisEngine()?.getLastResult()?.ciiScores;
      if (ciiScores && ciiScores.size > 0) {
        layers.push(...cachedFamily(
          layerId,
          `${analysisGeneration}|${staticDataGeneration}`,
          () => createCIIChoroplethLayer(ciiScores),
        ));
      }
      continue;
    }

    // Nuclear facilities — radiation icons + severity rings (static from @/config/data)
    if (NUCLEAR_LAYER_IDS.has(layerId)) {
      layers.push(...cachedFamily(layerId, staticSig, () => createNuclearLayers([], zoom)));
      continue;
    }

    // APT groups — static data plotted at state sponsor locations
    if (APT_LAYER_IDS.has(layerId)) {
      layers.push(...cachedFamily(layerId, staticSig, () => createAptLayers(zoom)));
      continue;
    }

    // Critical minerals — static deposit data
    if (MINERALS_LAYER_IDS.has(layerId)) {
      layers.push(...cachedFamily(layerId, staticSig, () => createMineralsLayers(zoom)));
      continue;
    }

    // IAEA irradiators — static facility data
    if (IRRADIATOR_LAYER_IDS.has(layerId)) {
      layers.push(...cachedFamily(layerId, staticSig, () => createIrradiatorLayers(zoom)));
      continue;
    }

    // Hotspot escalation — pulsing circles from analysis engine (no events)
    if (HOTSPOT_LAYER_IDS.has(layerId)) {
      layers.push(...createHotspotLayers(zoom));
      continue;
    }

    // Dark ships — vessels that went silent in shipping lanes (analysis-driven, no events)
    if (DARK_SHIP_LAYER_IDS.has(layerId)) {
      layers.push(...createDarkShipLayers(zoom));
      continue;
    }

    // Get events for this layer (O(1) lookup from pre-built index)
    const layerEvents = getEventsForLayer(layerId).filter((e) => e.location);

    // --- Live-data layer types (need events) ---

    // Heatmap layers (fires, earthquakes)
    if (layerId === 'fires') {
      layers.push(...cachedFamily(layerId, eventSig, () => createFireLayers(layerEvents, zoom)));
      continue;
    }
    if (layerId === 'earthquakes') {
      layers.push(...cachedFamily(layerId, eventSig, () => createEarthquakeLayers(layerEvents, zoom)));
      continue;
    }

    // Arc layers
    if (ARC_LAYER_IDS.has(layerId)) {
      layers.push(...cachedFamily(layerId, eventSig, () => [createArcLayer(layerId, layerEvents, zoom)]));
      continue;
    }

    // Track layers (vessels, flights)
    if (layerId === 'vessels') {
      layers.push(...cachedFamily(layerId, eventSig, () => createVesselLayers(layerEvents, zoom)));
      continue;
    }
    // Naval — military vessels only, WM's separate red-dot sign,
    // plus red-family fleet concentration discs
    if (MILITARY_VESSEL_LAYER_IDS.has(layerId)) {
      layers.push(...cachedFamily(layerId, eventSig, () => {
        const navalEvents = layerEvents.filter(isMilitaryEvent);
        return [
          ...createMilitaryVesselLayers(navalEvents),
          ...createMilitaryVesselClusterLayers(navalEvents, zoom),
        ];
      }));
      continue;
    }
    if (layerId === 'flights') {
      const trails = activeSet.has('flight-trails');
      layers.push(...cachedFamily(
        layerId,
        `${eventSig}|${trails}`,
        () => createFlightLayers(layerEvents, zoom, 'flights', trails),
      ));
      continue;
    }
    // Military aircraft — filtered to military callsigns, rendered as plain
    // dots (WM's civilian-vs-military shape contrast) plus blue-family
    // sortie concentration discs
    if (layerId === 'military-aircraft') {
      layers.push(...cachedFamily(layerId, eventSig, () => {
        const milFlights = layerEvents.filter(isMilitaryEvent);
        return [
          ...createMilitaryFlightLayers(milFlights),
          ...createMilitaryFlightClusterLayers(milFlights, zoom),
        ];
      }));
      continue;
    }

    // AIS density — aggregated grid, intensity-ramped blobs
    if (AIS_DENSITY_LAYER_IDS.has(layerId)) {
      layers.push(...cachedFamily(layerId, eventSig, () => createAisDensityLayers(layerEvents)));
      continue;
    }
    // AIS disruptions — anomaly-detector-driven, white-haloed markers
    if (AIS_DISRUPTION_LAYER_IDS.has(layerId)) {
      layers.push(...createAisDisruptionLayers());
      continue;
    }

    // Disaster layers (GDACS, volcanoes, tropical cyclones)
    if (layerId === 'gdacs') {
      layers.push(...cachedFamily(layerId, eventSig, () => createGdacsLayers(layerEvents, zoom)));
      continue;
    }
    if (layerId === 'volcanoes') {
      layers.push(...cachedFamily(layerId, eventSig, () => createVolcanoLayers(layerEvents, zoom)));
      continue;
    }
    if (layerId === 'tropical-cyclones') {
      layers.push(...cachedFamily(layerId, eventSig, () => createTropicalCycloneLayers(layerEvents, zoom)));
      continue;
    }

    // NOTAM closures — aviation restriction zones
    if (NOTAM_LAYER_IDS.has(layerId)) {
      layers.push(...cachedFamily(layerId, eventSig, () => createNotamLayers(layerEvents, zoom)));
      continue;
    }

    // UCDP typed conflict events
    if (UCDP_LAYER_IDS.has(layerId)) {
      layers.push(...cachedFamily(layerId, eventSig, () => createUcdpLayers(layerEvents, zoom)));
      continue;
    }

    // GPS jamming — H3 hex grid (must precede any generic point fallback)
    if (H3_JAMMING_LAYER_IDS.has(layerId)) {
      layers.push(...cachedFamily(layerId, eventSig, () => createH3JammingLayer(eventsToJamHexes(layerEvents))));
      continue;
    }

    // Radiation monitoring — white-ringed anomaly dots
    if (RADIATION_LAYER_IDS.has(layerId)) {
      layers.push(...cachedFamily(layerId, eventSig, () => createRadiationLayers(eventsToRadiationReadings(layerEvents))));
      continue;
    }

    // Cyber threats
    if (CYBER_LAYER_IDS.has(layerId)) {
      layers.push(...cachedFamily(layerId, eventSig, () => createCyberLayers(layerEvents, zoom)));
      continue;
    }

    // Infrastructure status — outages + cable advisories
    if (INFRA_STATUS_LAYER_IDS.has(layerId)) {
      layers.push(...cachedFamily(layerId, eventSig, () => createInfraStatusLayers(layerId, layerEvents, zoom)));
      continue;
    }

    // Displacement flows — scale-colored arcs
    if (DISPLACEMENT_LAYER_IDS.has(layerId)) {
      layers.push(...cachedFamily(layerId, eventSig, () => createDisplacementLayers(layerEvents, zoom)));
      continue;
    }

    // Weather alerts — severity-coded dots
    if (WEATHER_LAYER_IDS.has(layerId)) {
      layers.push(...cachedFamily(layerId, eventSig, () => createWeatherLayers(layerEvents, zoom)));
      continue;
    }

    // Ports + airports — type-specific icons (ports use static data, airports use events)
    if (PORT_LAYER_IDS.has(layerId)) {
      layers.push(...cachedFamily(
        layerId,
        `${eventSig}|${staticDataGeneration}`,
        () => createPortLayers(layerId, layerEvents, zoom),
      ));
      continue;
    }

    // News markers — threat-level coded by classifier. The breaking-news
    // pulse ring is time-varying (existence, fade, phase from Date.now())
    // and must be built fresh on every rebuild, outside the cache.
    if (NEWS_MARKER_LAYER_IDS.has(layerId)) {
      layers.push(...cachedFamily(layerId, eventSig, () => createNewsMarkerLayers(layerEvents, zoom)));
      const newsRing = createNewsMarkerPulseRing(layerEvents, zoom);
      if (newsRing) layers.push(newsRing);
      continue;
    }

    // Military activity — aggregated flight + vessel + conflict density grid
    if (MILITARY_ACTIVITY_LAYER_IDS.has(layerId)) {
      // Military activity needs events from multiple types, not a single source
      layers.push(...cachedFamily(layerId, eventSig, () => createMilitaryActivityLayers(events, zoom)));
      continue;
    }

    // Iran attack tracking — filters Iran-related events, draws directional arcs
    if (IRAN_ATTACK_LAYER_IDS.has(layerId)) {
      layers.push(...cachedFamily(layerId, eventSig, () => createIranAttackLayers(events, zoom)));
      continue;
    }

    // Prediction markets — Polymarket geo-located events as bubble markers
    if (PREDICTION_MARKET_LAYER_IDS.has(layerId)) {
      layers.push(...cachedFamily(layerId, eventSig, () => createPredictionMarketLayers(layerEvents, zoom)));
      continue;
    }

    // Satellites — real-time TLE propagation from CelesTrak
    if (SATELLITE_LAYER_IDS.has(layerId)) {
      if (layerId === 'satellite-imagery') {
        // Imagery footprints from event data
        const footprints = layerEvents
          .filter(e => e.raw && Array.isArray((e.raw as Record<string, unknown>).footprint))
          .map(e => ({
            id: e.id,
            polygon: (e.raw as Record<string, unknown>).footprint as [number, number][],
            satelliteName: e.title,
          }));
        if (footprints.length > 0) {
          layers.push(...cachedFamily(layerId, eventSig, () => createImageryFootprintLayers(footprints, zoom)));
        }
      } else if (satellitePositions.length > 0) {
        const showTracks = layerId === 'satellite-tracks' || activeSet.has('satellite-tracks');
        layers.push(...cachedFamily(
          layerId,
          `${satelliteGeneration}|${zoomBucket}|${showTracks}`,
          () => createSatelliteLayers(satellitePositions, zoom, showTracks),
        ));
      }
      continue;
    }

    // --- Clustered point layers ---
    const group = getClusterGroup(layerId);
    const clusterIndex = group ? clusterIndices.get(group) : undefined;

    if (clusterIndex && !renderedClusterGroups.has(group!)) {
      renderedClusterGroups.add(group!);

      // Cluster results depend on the floor'ed zoom (getClusters granularity),
      // the round bucket (getZoomAlpha on the unclustered individuals), the
      // content-cluster generation, and which sibling layers are active
      // (individuals filter below).
      const activeKey = [...activeLayers].sort().join(',');
      const clusterCacheKey = `cluster:${group}`;
      layers.push(...cachedFamily(
        clusterCacheKey,
        `${eventGeneration}|${eventFilterSignature}|${Math.floor(zoom)}|${zoomBucket}|${getContentClusterVersion()}|${activeKey}|${layerId}`,
        () => {
          const built: Layer[] = [];
          // Use clustering — get cluster results for current viewport
          const bbox: [number, number, number, number] = [-180, -85, 180, 85];
          const results = clusterIndex.getClusters(bbox, Math.floor(zoom));
          clusterResultsCache.set(clusterCacheKey, results);

          // Render cluster markers
          built.push(...createClusterLayers(group!, results, zoom));

          // Render individual (unclustered) points
          const individuals = results.filter((r) => !r.isCluster && r.event);
          if (individuals.length > 0) {
            const individualEvents = individuals
              .map((r) => r.event!)
              .filter((e) => getMatchingLayerId(e, activeSet) !== undefined);

            if (individualEvents.length > 0) {
              // Use icon layer for icon-type layers, else point layer
              if (ICON_LAYER_IDS.has(layerId)) {
                built.push(createIconLayer(layerId, individualEvents, zoom));
              } else {
                built.push(createPointLayer(`${group}-unclustered`, individualEvents, zoom));
              }
            }
          }
          return built;
        },
      ));

      // Protest pulse ring — time-varying phase, built fresh per rebuild
      // from the results the closure recorded (valid on a HIT too: the
      // signature pins every input of the getClusters call).
      const ringResults = clusterResultsCache.get(clusterCacheKey);
      if (ringResults) {
        const clusterRing = createClusterPulseLayer(group!, ringResults);
        if (clusterRing) layers.push(clusterRing);
      }
      continue;
    }

    // --- Icon layers (when not clustered) ---
    if (ICON_LAYER_IDS.has(layerId) && !clusterIndex) {
      layers.push(...cachedFamily(layerId, eventSig, () => [createIconLayer(layerId, layerEvents, zoom)]));
      continue;
    }

    // --- Default: generic point layer (when not clustered) ---
    if (POINT_LAYER_IDS.has(layerId) && !clusterIndex) {
      layers.push(...cachedFamily(layerId, eventSig, () => [createPointLayer(layerId, layerEvents, zoom)]));
      continue;
    }
  }

  // Pulse animation behind critical/high severity events (active layers only)
  const pulseEligibleEvents = events.filter((e) => {
    if (!e.location) return false;
    return getMatchingLayerId(e, activeSet) !== undefined;
  });
  const pulseLayer = createPulseLayer(pulseEligibleEvents, zoom);
  if (pulseLayer) {
    // Insert at beginning so it renders behind data layers
    layers.unshift(pulseLayer);
  }

  // Solar terminator (day/night boundary) — only when toggled on
  if (activeSet.has('day-night')) {
    layers.push(createTerminatorLayer());
  }

  // Label deconfliction — project high-severity events to screen and
  // determine which labels are visible without overlapping
  const map = getMap();
  if (map && zoom >= 4) {
    const labelCandidates: LabelCandidate[] = [];
    const labelEvents: NormalizedEvent[] = [];

    // Collect high-severity events with locations for label display
    for (const e of events) {
      if (!e.location) continue;
      const sev = e.severity ?? 'info';
      if (sev !== 'critical' && sev !== 'high') continue;

      const projected = map.project([e.location.lng, e.location.lat]);
      labelCandidates.push({
        x: projected.x,
        y: projected.y,
        severity: sev,
        timestamp: e.timestamp,
        id: e.id,
      });
      labelEvents.push(e);
    }

    if (labelCandidates.length > 0) {
      // NOTE: No rebuild callback — deconfliction results are used on the NEXT natural
      // rebuild cycle (zoom change, events change, etc). Calling scheduleRebuild() here
      // created a feedback loop: build → deconflict → rebuild → deconflict → rebuild...
      const visibleIds = scheduleDeconfliction(labelCandidates);

      const labelData = labelEvents.filter(e => visibleIds.has(e.id));

      if (labelData.length > 0) {
        layers.push(new TextLayer<NormalizedEvent>({
          id: 'event-labels',
          data: labelData,
          getPosition: (d) => d.location ? [d.location.lng, d.location.lat] : [0, 0],
          getText: (d) => d.title.length > 40 ? d.title.slice(0, 37) + '...' : d.title,
          getSize: 11,
          getColor: [255, 255, 255, 200],
          getTextAnchor: 'start' as const,
          getAlignmentBaseline: 'center' as const,
          getPixelOffset: [8, 0],
          fontFamily: 'monospace',
          sizeUnits: 'pixels' as const,
          billboard: true,
          outlineWidth: 2,
          outlineColor: [0, 0, 0, 180],
        }));
      }
    }
  }

  // WM's dev-mode frame-budget warning (DeckGLMap buildLayers) — gives an
  // observable console signal when a rebuild overruns a 60fps frame.
  if (import.meta.env.DEV) {
    const elapsed = performance.now() - buildStart;
    if (elapsed > 16) {
      console.warn(`[Sentinel] buildActiveLayers took ${elapsed.toFixed(2)}ms (>16ms budget), ${layers.length} layers`);
    }
  }

  return layers;
}

/**
 * Find the best matching layer ID for an event (for cluster de-interleaving).
 * With an activeSet, returns the first matching layer that is actually
 * active — a source's first-listed layer being toggled off must not hide
 * the event from its other layers (e.g. 'sanctions' wash off while
 * 'sanctions-markers' is on).
 */
function getMatchingLayerId(event: NormalizedEvent, activeSet?: Set<string>): string | undefined {
  const candidates = [
    ...(SOURCE_TO_LAYER[event.source] ?? []),
    ...(TYPE_TO_LAYER[event.type] ?? []),
  ];
  if (activeSet) {
    return candidates.find((id) => activeSet.has(id));
  }
  return candidates[0];
}

// --- Opacity transition system ---

const TRANSITION_DURATION_MS = 300;

/** Layers currently ramping opacity from 0→1. Maps layerId → start timestamp. */
const transitioningLayers = new Map<string, number>();

/** Current opacity for each transitioning layer (0..1). */
export const transitionOpacities = new Map<string, number>();

let transitionRafId: number | null = null;
let previousActiveLayers = new Set<string>();

/** Cached layer output from the last full buildActiveLayers() call. */
let cachedLayers: Layer[] = [];
let cachedActiveLayers: string[] = [];

/** Called when activeLayers change to detect newly enabled layers. */
function detectNewLayers(activeLayers: string[]): void {
  const currentSet = new Set(activeLayers);
  for (const id of currentSet) {
    if (!previousActiveLayers.has(id)) {
      // New layer — start opacity transition
      transitioningLayers.set(id, performance.now());
      transitionOpacities.set(id, 0);
    }
  }
  // Clean up removed layers
  for (const id of previousActiveLayers) {
    if (!currentSet.has(id)) {
      transitioningLayers.delete(id);
      transitionOpacities.delete(id);
    }
  }
  previousActiveLayers = currentSet;

  if (transitioningLayers.size > 0 && transitionRafId === null) {
    transitionRafId = requestAnimationFrame(tickTransitions);
  }
}

/**
 * Apply current opacity multipliers to cached layers without a full rebuild.
 * Used during transition animations to avoid 60Hz buildActiveLayers() calls.
 */
function applyTransitionOpacities(layers: Layer[], activeLayers: string[]): Layer[] {
  if (transitionOpacities.size === 0) return layers;

  return layers.map((layer) => {
    const deckId = layer.id;
    for (const layerId of activeLayers) {
      if (deckId === layerId || deckId.startsWith(`${layerId}-`) || deckId.startsWith(`point-${layerId}`) || deckId.startsWith(`icon-${layerId}`) || deckId.startsWith(`arc-${layerId}`) || deckId.startsWith(`geojson-${layerId}`) || deckId.startsWith(`static-point-${layerId}`) || deckId.startsWith(`trade-route-${layerId}`) || deckId.startsWith(`military-bases-`) || deckId.startsWith(`nuclear-`) || deckId.startsWith(`cyber-`) || deckId.startsWith(`gps-jamming-`) || deckId.startsWith(`outage-`) || deckId.startsWith(`cable-`) || deckId.startsWith(`displacement-`) || deckId.startsWith(`weather-`) || deckId.startsWith(`port-`) || deckId.startsWith(`airport-`) || deckId.startsWith(`news-marker-`) || deckId.startsWith(`hotspot-`) || deckId.startsWith(`military-activity-`) || deckId.startsWith(`dark-ship-`) || deckId.startsWith(`satellite`)) {
        const transitionOp = transitionOpacities.get(layerId);
        if (transitionOp !== undefined) {
          const currentOpacity = (layer.props as { opacity?: number }).opacity ?? 1;
          return layer.clone({ opacity: currentOpacity * transitionOp });
        }
        break;
      }
    }
    return layer;
  });
}

/** Animation frame loop for opacity transitions. */
function tickTransitions(): void {
  transitionRafId = null;
  const now = performance.now();
  let anyActive = false;

  for (const [id, startTime] of transitioningLayers) {
    const elapsed = now - startTime;
    const t = Math.min(elapsed / TRANSITION_DURATION_MS, 1);
    transitionOpacities.set(id, t);

    if (t >= 1) {
      transitioningLayers.delete(id);
      transitionOpacities.delete(id);
    } else {
      anyActive = true;
    }
  }

  // Re-apply opacity multipliers to cached layers (no full rebuild)
  if (cachedLayers.length > 0) {
    const withOpacity = applyTransitionOpacities(cachedLayers, cachedActiveLayers);
    setDeckLayers(withOpacity);
  }

  if (anyActive) {
    transitionRafId = requestAnimationFrame(tickTransitions);
  }
}

/**
 * Get the current opacity multiplier for a layer.
 * Returns 1.0 if not transitioning, otherwise 0..1.
 */
export function getLayerOpacity(layerId: string): number {
  return transitionOpacities.get(layerId) ?? 1;
}

// --- Store subscription and initialization ---

let debounceTimer: ReturnType<typeof setTimeout> | null = null;
let unsubscribers: (() => void)[] = [];

// --- Weather radar (MapLibre raster, lives outside the deck.gl layer array) ---

const WEATHER_RADAR_REFRESH_MS = 5 * 60 * 1000;
let weatherRadarActive = false;
let weatherRadarTimer: ReturnType<typeof setInterval> | null = null;
// Serializes async add/remove so rapid toggles can never interleave a stale
// add after a remove (or leave an orphaned raster behind).
let weatherRadarChain: Promise<void> = Promise.resolve();

function clearWeatherRadarTimer(): void {
  if (weatherRadarTimer !== null) {
    clearInterval(weatherRadarTimer);
    weatherRadarTimer = null;
  }
}

/** Add/remove the RainViewer raster overlay when its toggle changes. */
function syncWeatherRadar(activeLayers: string[]): void {
  const wanted = activeLayers.includes('weather-radar');
  if (wanted === weatherRadarActive) return;

  const map = getMap();
  if (!map) return; // Map not ready — retried on the next activeLayers change

  weatherRadarActive = wanted;
  clearWeatherRadarTimer();

  weatherRadarChain = weatherRadarChain
    .then(async () => {
      // Act on the LATEST desired state; superseded steps become no-ops
      // (add/remove are both idempotent).
      if (weatherRadarActive) {
        await addWeatherRadarLayer(map);
        if (!weatherRadarActive) {
          // Toggled off while the tiles were loading
          removeWeatherRadarLayer(map);
          return;
        }
        clearWeatherRadarTimer();
        weatherRadarTimer = setInterval(() => {
          updateWeatherRadar(map).catch(() => {
            // Transient RainViewer failure — the stale frame stays up
          });
        }, WEATHER_RADAR_REFRESH_MS);
      } else {
        removeWeatherRadarLayer(map);
      }
    })
    .catch((err) => {
      weatherRadarActive = false;
      clearWeatherRadarTimer();
      console.warn('[Sentinel] Weather radar failed to load:', err);
    });
}

/**
 * Signature of the time-filter inputs that shape the filtered event set.
 * Sliding ranges anchor their cutoff in Date.now() (time-filter.ts), so a
 * minute bucket forces a re-index/rebuild as wall-clock time moves —
 * without it, events that aged out of the window stayed on the map until
 * the next store-events change. Exported for tests.
 */
export function timeFilterSignature(state: { timeRange: string; timeCursor: number | null }): string {
  const minuteBucket = state.timeRange !== 'all' ? Math.floor(Date.now() / 60_000) : '';
  return `${state.timeRange}|${state.timeCursor ?? ''}|${minuteBucket}`;
}

/** WM rebuild debounce (catalog section 17: 150ms + RAF-based update batching). */
const REBUILD_DEBOUNCE_MS = 150;

/** The actual rebuild — runs on an animation frame via rafRebuild below. */
function runRebuild(): void {
  const state = appStore.getState();
  const filtered = filterEventsByTimeRange(state.events, state.timeRange, state.timeCursor);
  const layers = buildActiveLayers(
    filtered, state.activeLayers, state.viewport.zoom, timeFilterSignature(state),
  );
  // Cache raw layers for transition system to re-apply opacities without full rebuild
  cachedLayers = layers;
  cachedActiveLayers = state.activeLayers;
  // Apply any active transition opacities before rendering
  const withOpacity = applyTransitionOpacities(layers, state.activeLayers);
  setDeckLayers(withOpacity);

  // Start/stop the pulse driver based on whether a pulse layer was created
  pulseDriver.sync();
}

/** RAF batcher — bursts from the debounce and other callers collapse into one build per frame. */
const rafRebuild = rafSchedule(runRebuild);

/**
 * WM pulse driver: 500ms interval through the layer-cached rebuild path,
 * input-pending skip, 60s startup grace. WM gates its pulse on breaking-news/
 * riot recency signals Sentinel does not populate yet — the adaptation gates
 * on the populated equivalent: a pulse layer exists (critical/high events on
 * active layers within the current time filter).
 */
/** Layer ids whose animation the pulse driver must keep ticking. The two
 *  rings are time-varying and rebuilt fresh per rebuild — the driver's
 *  ticks are what animate (and expire) them. */
const PULSE_ANIMATED_LAYER_IDS = new Set([
  'pulse-critical',
  'news-marker-pulse',
  'cluster-pulse-protests',
]);

const pulseDriver = createPulseDriver({
  needsPulse: () => cachedLayers.some((l) => PULSE_ANIMATED_LAYER_IDS.has(l.id)),
  requestRebuild: () => rafRebuild(),
});

/** Debounced layer rebuild. Waits 150ms after last state change, then builds on the next frame. */
function scheduleRebuild(): void {
  if (debounceTimer !== null) {
    clearTimeout(debounceTimer);
  }
  debounceTimer = setTimeout(() => {
    debounceTimer = null;
    rafRebuild();
  }, REBUILD_DEBOUNCE_MS);
}

/**
 * Initialize the layer rendering system.
 * Sets up store subscriptions to watch activeLayers, events, and viewport.zoom.
 * Triggers initial GeoJSON preload for active layers.
 */
export function initLayerSystem(): void {
  // Clean up previous subscriptions if re-initialized
  destroyLayerSystem();

  // Initialize analysis-driven layers
  initHotspotLayer();
  initDarkShipLayer();
  initAisDisruptionLayer();

  // Seed previous active layers to avoid transition on initial load
  previousActiveLayers = new Set(appStore.getState().activeLayers);

  // Watch active layers
  unsubscribers.push(
    appStore.subscribe(
      (s) => s.activeLayers,
      (activeLayers) => {
        detectNewLayers(activeLayers);
        syncWeatherRadar(activeLayers);
        // Rebuild immediately with whatever is cached (non-GeoJSON layers appear instantly)
        scheduleRebuild();
        // Backfill GeoJSON data, then rebuild again to show borders/chokepoints
        preloadGeoJsonLayers(activeLayers).then(() => {
          bumpStaticDataGeneration();
          scheduleRebuild();
        });
      },
    ),
  );

  // Watch events — bump generation counter so cluster cache invalidates correctly
  // Content clustering runs in background but does NOT trigger a second rebuild.
  // The clusters are picked up on the NEXT scheduled rebuild (from zoom/timer/etc).
  unsubscribers.push(
    appStore.subscribe(
      (s) => s.events,
      () => {
        bumpEventGeneration();
        scheduleRebuild();

        // Run content clustering in Web Worker. Its result version (bumped at
        // result-LANDING inside content-clustering, not on this promise — a
        // superseded promise resolves with the wrong arrival) invalidates the
        // cluster guards, so results land on the next scheduled rebuild.
        // Still no second rebuild scheduled here.
        const state = appStore.getState();
        const filtered = filterEventsByTimeRange(state.events, state.timeRange, state.timeCursor);
        runContentClustering(filtered).catch(() => {
          // Timeout or error — spatial-only clustering continues to work
        });
      },
    ),
  );

  // Watch viewport zoom (rounded to avoid excessive rebuilds on smooth zoom)
  unsubscribers.push(
    appStore.subscribe(
      (s) => Math.round(s.viewport.zoom),
      () => scheduleRebuild(),
    ),
  );

  // Watch time range changes
  unsubscribers.push(
    appStore.subscribe(
      (s) => s.timeRange,
      () => scheduleRebuild(),
    ),
  );

  // Watch time cursor changes (time slider scrubbing)
  unsubscribers.push(
    appStore.subscribe(
      (s) => s.timeCursor,
      () => scheduleRebuild(),
    ),
  );

  // Country hover highlight — triggers rebuild on hover change
  unsubscribers.push(initCountryHighlight(() => scheduleRebuild()));

  // Country geometry loads async — once it lands, conflict zones upgrade to
  // real country outlines and the CII choropleth can paint. Idempotent.
  initCountryDetection().then(() => {
    bumpStaticDataGeneration();
    scheduleRebuild();
  });

  // Analysis cycles refresh CII scores — repaint the choropleth when done
  unsubscribers.push(eventBus.on('analysis:complete', () => {
    analysisGeneration++;
    scheduleRebuild();
  }));

  // Solar terminator — update every 60 seconds
  startTerminatorUpdates(() => scheduleRebuild());

  // Satellite positions — initial fetch + refresh every 60 seconds for real-time tracking
  refreshSatelliteData();
  satelliteFetchTimer = setInterval(refreshSatelliteData, 60_000);

  // Initial build — render immediately with static data (GeoJSON layers show empty until fetched)
  const state = appStore.getState();
  syncWeatherRadar(state.activeLayers);
  const filtered = filterEventsByTimeRange(state.events, state.timeRange, state.timeCursor);
  const layers = buildActiveLayers(
    filtered, state.activeLayers, state.viewport.zoom, timeFilterSignature(state),
  );
  cachedLayers = layers;
  cachedActiveLayers = state.activeLayers;
  const withOpacity = applyTransitionOpacities(layers, state.activeLayers);
  setDeckLayers(withOpacity);

  // Backfill GeoJSON data async, then rebuild to show borders/chokepoints
  preloadGeoJsonLayers(state.activeLayers).then(() => {
    bumpStaticDataGeneration();
    scheduleRebuild();
  });
}

/** Tear down the layer system and clean up subscriptions. */
export function destroyLayerSystem(): void {
  for (const unsub of unsubscribers) {
    unsub();
  }
  unsubscribers = [];

  clearWeatherRadarTimer();
  if (weatherRadarActive) {
    weatherRadarActive = false;
    // Chained so an in-flight add settles before the teardown remove runs
    weatherRadarChain = weatherRadarChain
      .then(() => {
        const map = getMap();
        if (map) removeWeatherRadarLayer(map);
      })
      .catch(() => {
        // Add failed — nothing left to remove
      });
  }

  if (debounceTimer !== null) {
    clearTimeout(debounceTimer);
    debounceTimer = null;
  }
  rafRebuild.cancel();

  if (transitionRafId !== null) {
    cancelAnimationFrame(transitionRafId);
    transitionRafId = null;
  }

  pulseDriver.stop();

  transitioningLayers.clear();
  transitionOpacities.clear();
  previousActiveLayers.clear();
  cachedLayers = [];
  cachedActiveLayers = [];
  clusterIndices.clear();
  lastClusterEventCount = -1;
  lastClusterGeneration = -1;
  lastClusterFilterSignature = '';
  lastClusterContentGeneration = -1;
  lastEventIndexGeneration = -1;
  lastEventFilterSignature = '';
  layerInstanceCache.clear();
  clusterResultsCache.clear();
  clearPositionHistory();
  destroyLabelDeconfliction();
  destroyCountryHighlight();
  destroyContentClustering();
  stopTerminatorUpdates();
  destroyHotspotLayer();
  destroyDarkShipLayer();
  destroyAisDisruptionLayer();

  if (satelliteFetchTimer !== null) {
    clearInterval(satelliteFetchTimer);
    satelliteFetchTimer = null;
  }
  satellitePositions = [];
}
